import {
  faqs,
  howItWorks,
  CheckoutTerms,
  CheckoutSuccessDescription,
  NoInventoryModalDescription,
} from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/rangers/rangers_3d_venue.png";

const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/NHL/NYR2627_Account%20Manager%20Header.png";
const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/rangers-logo2-aug21.png";

// Registration/auth page image = images.authBackgroundImage (MSG-provided texture).
const RegistrationImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/NYR2526%20-%20Texture%20-%20Backgrounds%20-%206%20-%201920x640.png";
// Purchase confirmation / transfer screen backgrounds (MSG-provided): desktop
// texture + mobile variant (images.checkoutStatusPageImage / mobile fallback).
const CheckoutStatusBg =
  "https://ss-leagues.s3.amazonaws.com/NHL/NYR2526%20-%20Texture%20-%20Backgrounds%20-%206%20-%201920x640.png";
const MobileCheckoutStatusBg =
  "https://ss-leagues.s3.amazonaws.com/NHL/MSG%20background%201.png";

const config: TeamConfig = {
  teamSchema: "newyorkrangers_groups",
  browserTitle: "Rangers Groups",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "New York Rangers",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00045-rangers-sec",
    mapId: "main",
    isGroupTicketSeatMapEnabled: true,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: true,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.groupLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 9,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 9,
    checkoutTimerEnabled: false,
    isPerksEnabled: true,

    isTicketTransferEnabled: true,
    hideViewTicketsInThankYouPage: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/msg/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    authBackgroundImage: RegistrationImage,
    findGamesBannerImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: CheckoutStatusBg,
    mobileCheckoutStatusPageImage: MobileCheckoutStatusBg,
    logoImage: LogoImage + "?width=76&height=76",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    // Checkout subtotal label (was "Add-Ons Balance")
    checkoutPerkSubtotals: {
      perkTotalLabel: "27-28 Deposit Balance",
    },

    // Remove the tint overlay on the confirmation/transfer screen so the
    // MSG background image shows through unmodified.
    checkoutStatusPage: {
      overlayOpacity: 0,
    },
  },

  colors: {
    primary: "#005189",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <CheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Select Continue to view seating options.",
      // Stepper tab label + add-ons viewer heading (was "Add-Ons")
      chooseAddOnsTabTitle: "27-28 Deposit",
      addOnsLabel: "27-28 Deposit",
      // Checkout "Exclusive Perks" section heading override
      exclusivePerksLabel: "Group Deposit",
      // FindGames zero-state (see FindGames.tsx) — TM account groups can take a
      // few minutes to sync after login, so tell the user to retry + re-auth
      noSessionsAvailable: "No events available.",
      noSessionsAvailableDescription:
        "Please wait 15 minutes before trying again and refresh your login.",
    },
    // Perk price unit label: renders "$250.00 for 1 deposit" instead of
    // "$250.00 for 1 seat" (see utils.perks.getPerkPriceUnitName)
    perksPage: {
      perkItemPriceLabel: "deposit",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    noInventoryAlertModal: {
      description: {
        inventoryApiTriggered: <NoInventoryModalDescription />,
        minRestrictionOn: <NoInventoryModalDescription />,
        minRestrictionOff: <NoInventoryModalDescription />,
      },
    },

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: <CheckoutSuccessDescription />,

    gameFilters: {
      seatCounter: {
        description:
          "Select the number of people in your group. Minimum of 9 (up to 99) required.",
      },
    },

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
